import React, { useEffect, useRef, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

const MONTH_NAMES = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

const MONTH_NAMES_FULL = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function getCurrentDate(date: Date) {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}`;
}

function formatMonthLabel(value: string) {
  const [year, month] = value.split("-");
  const monthIndex = parseInt(month, 10) - 1;
  const monthName = MONTH_NAMES_FULL[monthIndex] ?? month;
  return `${monthName} de ${year}`;
}

function parseValue(value: string) {
  const [year, month] = value.split("-");
  return {
    year: parseInt(year, 10),
    month: parseInt(month, 10),
  };
}

export default function MonthPicker({
  value,
  onDateSelect,
  label = "Mês",
}: {
  value?: string;
  onDateSelect: (date: string) => void;
  label?: string;
}) {
  const defaultValue = value ?? getCurrentDate(new Date());
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(parseValue(defaultValue).year);
  const containerRef = useRef<HTMLDivElement>(null);

  const { month: selectedMonth } = parseValue(selectedValue);

  useEffect(() => {
    if (value) {
      setSelectedValue(value);
      setViewYear(parseValue(value).year);
    }
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSelectMonth(month: number) {
    const nextValue = `${viewYear}-${month.toString().padStart(2, "0")}`;
    setSelectedValue(nextValue);
    onDateSelect(nextValue);
    setIsOpen(false);
  }

  function openPicker() {
    setViewYear(parseValue(selectedValue).year);
    setIsOpen((prev) => !prev);
  }

  return (
    <div ref={containerRef} className="relative w-full sm:w-auto">
      {label && (
        <span className="mb-1.5 block text-[0.65rem] font-semibold uppercase tracking-wider text-[#6e7681]">
          {label}
        </span>
      )}
      <button
        type="button"
        onClick={openPicker}
        className="flex w-full min-w-[200px] items-center gap-2.5 rounded-2xl border border-white/[0.08] bg-[#1c2128] px-4 py-3 text-left transition-colors hover:border-[#6ea8ff]/30 focus:border-[#6ea8ff]/40 focus:outline-none focus:ring-2 focus:ring-[#6ea8ff]/20 sm:w-auto"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Calendar className="h-4 w-4 shrink-0 text-[#6ea8ff]" />
        <span className="flex-1 truncate text-sm font-medium text-[#f0f6fc]">
          {formatMonthLabel(selectedValue)}
        </span>
        <ChevronRight className={`h-4 w-4 shrink-0 text-[#8b949e] transition-transform ${isOpen ? "rotate-90" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-[280px] rounded-2xl border border-white/[0.08] bg-[#1c2128] p-4 shadow-[0_12px_40px_rgba(0,0,0,0.5)] sm:left-auto">
          <div className="mb-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setViewYear((year) => year - 1)}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-[#8b949e] transition-colors hover:border-[#6ea8ff]/30 hover:bg-[#6ea8ff]/10 hover:text-[#6ea8ff]"
              aria-label="Ano anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-bold text-[#f0f6fc]">{viewYear}</span>
            <button
              type="button"
              onClick={() => setViewYear((year) => year + 1)}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-[#8b949e] transition-colors hover:border-[#6ea8ff]/30 hover:bg-[#6ea8ff]/10 hover:text-[#6ea8ff]"
              aria-label="Próximo ano"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {MONTH_NAMES.map((monthLabel, index) => {
              const monthNumber = index + 1;
              const isSelected =
                viewYear === parseValue(selectedValue).year && monthNumber === selectedMonth;
              const isCurrentMonth =
                viewYear === new Date().getFullYear() && monthNumber === new Date().getMonth() + 1;

              return (
                <button
                  key={monthLabel}
                  type="button"
                  onClick={() => handleSelectMonth(monthNumber)}
                  className={`rounded-xl px-2 py-2.5 text-xs font-semibold transition-colors ${
                    isSelected
                      ? "bg-[#6ea8ff] text-[#0d1117]"
                      : isCurrentMonth
                        ? "border border-[#6ea8ff]/40 bg-[#6ea8ff]/10 text-[#6ea8ff]"
                        : "border border-transparent text-[#f0f6fc] hover:bg-white/5"
                  }`}
                >
                  {monthLabel}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
